import { useApi } from '@classgo/app-kit';
import type { AlbertLevel, AlbertMaterialKind, AlbertMaterialView } from '@classgo/shared';
import { Button, Input, Select, useToast } from '@classgo/ui';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { albertApi } from '../../albert/api';
import { recentMaterials } from '../../albert/recent-materials';
import type { RecentMaterial } from '../../albert/types';
import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';
import { RefusalNotice } from './refusal-notice';
import { formatDateTime } from './support';
import type { PanelProps } from './types';

const KINDS: AlbertMaterialKind[] = ['questions', 'worksheet', 'rubric'];
const LEVELS: AlbertLevel[] = ['basico', 'medio', 'avanzado'];

/**
 * A download link as the screen prints it: the document opens in a new tab (the file is HTML,
 * the browser renders and prints it) and the link is what the teacher shares or bookmarks
 * within its 15 minutes.
 */
const DownloadLink = ({ material, label }: { material: AlbertMaterialView; label: string }) => (
  <a
    href={material.downloadUrl}
    target="_blank"
    rel="noreferrer"
    className="font-medium text-brand-700 underline underline-offset-2 hover:decoration-2"
  >
    {label}
  </a>
);

/**
 * Documents for the topic: kind and level, optional differentiation notes, "Generar material",
 * then the document's title, size and "Descargar". The documents generated from this browser
 * stay listed under the topic with "Nuevo enlace", which asks the API for a fresh link.
 */
export const MaterialPanel = ({ pair, topic, onQuota }: PanelProps) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const { session } = useTenant();
  const memory = useMemo(
    () => recentMaterials(window.localStorage, session.tenant.id, session.user.id),
    [session.tenant.id, session.user.id],
  );
  const [kind, setKind] = useState<AlbertMaterialKind>('worksheet');
  const [level, setLevel] = useState<AlbertLevel>('medio');
  const [notes, setNotes] = useState('');
  const [latest, setLatest] = useState<AlbertMaterialView | null>(null);
  const [recent, setRecent] = useState<RecentMaterial[]>(() => memory.ofTopic(topic.id));
  const [links, setLinks] = useState<Record<string, AlbertMaterialView>>({});

  const generate = useMutation({
    mutationFn: () =>
      albertApi(api).generateMaterial({
        topicId: topic.id,
        groupId: pair.groupId,
        kind,
        level,
        ...(notes.trim() ? { focus: notes.trim() } : {}),
      }),
    onSuccess: (result) => {
      onQuota(result.quota);
      setLatest(result.material);
      setRecent(memory.remember(result.material));
      toast.show({ tone: 'success', title: t('albert.material.ready') });
    },
  });

  const relink = useMutation({
    mutationFn: (id: string) => albertApi(api).refreshMaterial(id),
    onSuccess: (material) => {
      setLinks((current) => ({ ...current, [material.id]: material }));
      toast.show({ tone: 'success', title: t('albert.material.relinked') });
    },
    onError: (error) => {
      toast.show({
        tone: 'error',
        title: t('albert.material.relink'),
        description: (error as Error).message,
      });
    },
  });

  const working = generate.isPending;
  const previous = recent.filter((row) => row.id !== latest?.id);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('albert.material.kind')}
          value={kind}
          onChange={(event) => setKind(event.target.value as AlbertMaterialKind)}
          options={KINDS.map((value) => ({ value, label: t(`albert.material.kind.${value}`) }))}
          disabled={working}
        />
        <Select
          label={t('albert.material.level')}
          value={level}
          onChange={(event) => setLevel(event.target.value as AlbertLevel)}
          options={LEVELS.map((value) => ({ value, label: t(`albert.material.level.${value}`) }))}
          disabled={working}
        />
      </div>
      <Input
        label={t('albert.material.notes')}
        help={t('albert.material.notes.help')}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        maxLength={300}
        disabled={working}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-gray-500">{t('albert.material.review')}</p>
        <Button loading={working} onClick={() => generate.mutate()}>
          {t('albert.material.generate')}
        </Button>
      </div>
      {working ? (
        <p role="status" className="text-sm text-gray-600">
          {t('albert.material.working')}
        </p>
      ) : null}
      {generate.isError && !working ? (
        <RefusalNotice
          error={generate.error}
          retrying={working}
          onRetry={() => generate.mutate()}
        />
      ) : null}
      {latest ? (
        <section
          aria-label={t('albert.material.ready')}
          className="rounded-card border border-success-600/30 bg-success-100 px-4 py-3 text-sm text-gray-900"
        >
          <p className="font-medium">{latest.title}</p>
          <p className="mt-1 text-xs text-gray-600">
            {t(`albert.material.kind.${latest.kind}`)} ·{' '}
            {t(`albert.material.level.${latest.level}`)} · {latest.filename} ·{' '}
            {t('albert.material.size', { kb: Math.ceil(latest.sizeBytes / 1024) })}
          </p>
          <p className="mt-2">
            <DownloadLink
              material={links[latest.id] ?? latest}
              label={t('albert.material.download')}
            />
          </p>
          <p className="mt-1 text-xs text-gray-600">{t('albert.material.download.help')}</p>
        </section>
      ) : null}
      {previous.length > 0 ? (
        <section aria-label={t('albert.material.recent')} className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('albert.material.recent')}</h3>
          <p className="text-xs text-gray-500">{t('albert.material.recent.help')}</p>
          <ul className="divide-y divide-gray-200 rounded-card border border-gray-200">
            {previous.map((row) => (
              <li
                key={row.id}
                className="flex flex-col gap-2 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium text-gray-900">{row.title}</p>
                  <p className="text-xs text-gray-500">
                    {t(`albert.material.kind.${row.kind}`)} ·{' '}
                    {t(`albert.material.level.${row.level}`)} · {formatDateTime(row.createdAt)}
                  </p>
                </div>
                {links[row.id] ? (
                  <DownloadLink
                    material={links[row.id] as AlbertMaterialView}
                    label={t('albert.material.download')}
                  />
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={relink.isPending && relink.variables === row.id}
                    onClick={() => relink.mutate(row.id)}
                  >
                    {t('albert.material.relink')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};
